import { LineChartUp02 } from "@untitledui/icons";
import { Button as AriaButton, type Selection } from "react-aria-components";
import { CheckboxBase } from "@/components/base/checkbox/checkbox";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { cx } from "@/utils/cx";
import { METRICS, type MetricGroup, type MetricKey } from "../lib/metrics";
import { applyPinSelection } from "../lib/pins";

const GROUPS: MetricGroup[] = ["GMV", "Samples", "Videos", "Creators"];

type CustomisePopoverProps = {
  pins: MetricKey[];
  onChange: (pins: MetricKey[]) => void;
  onReset: () => void;
};

const selectedKeys = (selection: Selection): ReadonlySet<string> =>
  selection === "all" ? new Set(METRICS.map((metric) => metric.key)) : new Set([...selection].map(String));

export function CustomisePopover({ pins, onChange, onReset }: CustomisePopoverProps) {
  return (
    <Dropdown.Root>
      <AriaButton className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg px-2 py-1 text-sm font-medium text-[#21133a]/80 outline-focus-ring hover:text-[#21133a] focus-visible:outline-2">
        <LineChartUp02 width={14} height={14} aria-hidden="true" />
        Customise
      </AriaButton>
      <Dropdown.Popover placement="bottom end" className="w-auto p-4">
        <p className="mb-3 text-[13px] font-semibold text-[#21133a]">Choose the metrics to show</p>
        <Dropdown.Menu
          selectionMode="multiple"
          disallowEmptySelection={false}
          selectedKeys={new Set(pins)}
          onSelectionChange={(selection) => onChange(applyPinSelection(pins, selectedKeys(selection)))}
          className="flex items-start gap-6 py-0"
        >
          {GROUPS.map((group) => (
            <Dropdown.Section key={group} className="flex min-w-[150px] flex-col gap-1">
              <Dropdown.SectionHeader className="text-[11px] font-semibold tracking-[0.08em] text-[#21133a]/50 uppercase">
                {group}
              </Dropdown.SectionHeader>
              {METRICS.filter((metric) => metric.group === group).map((metric) => (
                <Dropdown.Item
                  key={metric.key}
                  id={metric.key}
                  textValue={metric.label}
                  unstyled
                  className={(state) =>
                    cx(
                      "flex cursor-pointer items-center gap-2 rounded-md px-1 py-1 text-[13px] text-[#21133a] outline-hidden",
                      state.isFocused && "bg-primary_hover",
                    )
                  }
                >
                  {(state) => (
                    <>
                      <CheckboxBase isSelected={state.isSelected} isFocusVisible={state.isFocusVisible} />
                      <span className="min-w-0 flex-1 truncate">{metric.label}</span>
                    </>
                  )}
                </Dropdown.Item>
              ))}
            </Dropdown.Section>
          ))}
        </Dropdown.Menu>
        <button
          type="button"
          onClick={onReset}
          className="mt-4 cursor-pointer text-[13px] font-medium text-[#21133a]/70 hover:text-[#21133a] hover:underline"
        >
          Reset to default
        </button>
      </Dropdown.Popover>
    </Dropdown.Root>
  );
}
